import { useEffect, useMemo, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Temporal } from 'temporal-polyfill';
import { formatPlainTime } from '@airreading/common';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/components/ui/hooks/use-toast';
import { getDayOfWeekOptions } from '@/lib/date-utils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DayOfWeek, DayOfWeekLabelMap, TimeRange } from '@airreading/common/data';
import { FormInputTimeColumn } from '../forms/components/form-input-time-columns';
import { computeValidTimeColumns, TimeMeridiem, to24HourFormat } from '../forms/components/form-input-time-utils';
import { booleanOnlyValueOf } from '../ui/multi-select-utils';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';

interface TableFilterTimeRangePickerProps {
  placeholder?: string;
  selectedRanges: TimeRange[];
  onClear?: () => void;
  onChange: (ranges: TimeRange[]) => void;
  untakenTeachersOnly: boolean;
  onUntakenTeachersOnlyChange: (untakenTeachersOnly: boolean) => void;
}

export const TableFilterTimeRangePicker = ({
  selectedRanges,
  onClear,
  placeholder = 'Time range',
  untakenTeachersOnly = true,
  onUntakenTeachersOnlyChange,
  onChange,
}: TableFilterTimeRangePickerProps) => {
  const [draftTime, setDraftTime] = useState({
    dayOfWeek: DayOfWeek.monday,
    startHour: '',
    startMinute: '',
    startPeriod: TimeMeridiem.AM,
    endHour: '',
    endMinute: '',
    endPeriod: TimeMeridiem.AM,
  });
  const [timeRanges, setTimeRanges] = useState<TimeRange[]>([]);
  const { toast } = useToast();

  // Sync internal state with prop
  useEffect(() => {
    setTimeRanges(selectedRanges ?? []);
  }, [selectedRanges]);
  const isUntakenTeachersOnlyChecked = untakenTeachersOnly ?? true;

  const dayOptions = useMemo(() => getDayOfWeekOptions(), []);

  const addTimeRange = () => {
    const startHour24 = to24HourFormat(parseInt(draftTime.startHour, 10), draftTime.startPeriod);
    const startPlainTime = Temporal.PlainTime.from({ hour: startHour24, minute: parseInt(draftTime.startMinute, 10) });

    const endHour24 = to24HourFormat(parseInt(draftTime.endHour, 10), draftTime.endPeriod);
    const endPlainTime = Temporal.PlainTime.from({ hour: endHour24, minute: parseInt(draftTime.endMinute, 10) });

    if (Temporal.PlainTime.compare(startPlainTime, endPlainTime) >= 0) {
      toast({
        title: 'Start time must be before end time',
      });
      return;
    }

    const newRange = { day: draftTime.dayOfWeek, start: startPlainTime.toString(), end: endPlainTime.toString() };

    setTimeRanges((prev) => {
      const next = [...prev, newRange];
      onChange(next);
      return next;
    });

    setDraftTime({
      dayOfWeek: DayOfWeek.monday,
      startHour: '',
      startMinute: '',
      startPeriod: TimeMeridiem.AM,
      endHour: '',
      endMinute: '',
      endPeriod: TimeMeridiem.AM,
    });
  };

  const set = (key: keyof typeof draftTime) => (value: string) => setDraftTime((draft) => ({ ...draft, [key]: value }));

  const step = Temporal.Duration.from({ minutes: 1 });
  const min = Temporal.PlainTime.from('00:00:00');
  const max = Temporal.PlainTime.from('23:59:00');
  const { validHours, validMinutes, validMeridiems } = useMemo(
    () => computeValidTimeColumns(step, min, max),
    [step, min, max],
  );

  const removeTimeRange = (index: number) => {
    setTimeRanges((prev) => {
      const next = prev.filter((_, i) => i !== index);
      onChange(next);
      return next;
    });
  };

  const formatTime = (plainTimeStr: string) => {
    const plainTime = Temporal.PlainTime.from(plainTimeStr);
    return formatPlainTime(plainTime, { hour12: true });
  };

  const formatRange = (range: { start: string; end: string }) =>
    `${formatTime(range.start)} - ${formatTime(range.end)}`;

  const dayOfWeekDropdown = (
    <Select value={draftTime.dayOfWeek || dayOptions[0]?.value || ''} onValueChange={set('dayOfWeek')}>
      <SelectTrigger className="justify-center h-8 px-0">
        <SelectValue>
          {dayOptions.find((opt) => opt.value === draftTime.dayOfWeek)?.label.slice(0, 3) ||
            DayOfWeekLabelMap[DayOfWeek.monday].abbr}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {dayOptions.map((opt) => (
          <SelectItem key={opt.value} value={opt.value}>
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  const TimeUnitDropdown = ({
    options,
    value,
    onChange,
    placeholder = '',
    className = 'w-[50px]',
  }: {
    options: string[];
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    className?: string;
  }) => (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className={`${className} justify-center`}>
          {value || placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-1 w-auto">
        <FormInputTimeColumn options={options} value={value} onValueChange={(value) => onChange(value)} />
      </PopoverContent>
    </Popover>
  );

  return (
    <>
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="h-8 border-dashed">
            {placeholder}
            {timeRanges.length > 0 && (
              <>
                <Separator orientation="vertical" className="mx-2 h-4" />
                <Badge variant="secondary" className="rounded-sm px-1 font-normal">
                  {timeRanges.length === 1
                    ? `${DayOfWeekLabelMap[timeRanges[0].day as DayOfWeek].abbr} ${formatRange(timeRanges[0])}`
                    : 'Multiple ranges'}
                </Badge>
              </>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <div className="flex flex-row items-center gap-2 p-4">
            {dayOfWeekDropdown}
            <TimeUnitDropdown
              options={validHours}
              value={draftTime.startHour.toString()}
              onChange={set('startHour')}
              placeholder="hh"
            />
            <TimeUnitDropdown
              options={validMinutes}
              value={draftTime.startMinute.toString()}
              onChange={set('startMinute')}
              placeholder="mm"
            />
            <TimeUnitDropdown
              options={validMeridiems}
              value={draftTime.startPeriod}
              onChange={set('startPeriod')}
              placeholder="am"
            />
            <p className="self-center">-</p>
            <TimeUnitDropdown
              options={validHours}
              value={draftTime.endHour.toString()}
              onChange={set('endHour')}
              placeholder="hh"
            />
            <TimeUnitDropdown
              options={validMinutes}
              value={draftTime.endMinute.toString()}
              onChange={set('endMinute')}
              placeholder="mm"
            />
            <TimeUnitDropdown
              options={validMeridiems}
              value={draftTime.endPeriod}
              onChange={set('endPeriod')}
              placeholder="am"
            />
            <Button variant="default" size="sm" className="h-10 ml-16" type="button" onClick={addTimeRange}>
              Add
            </Button>
          </div>
          {timeRanges.length > 0 && (
            <div className="px-4 pb-3">
              <div className="flex flex-col gap-1.5">
                {timeRanges.map((range, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between rounded-md px-2 py-1 text-sm bg-[#F3F4F6]"
                  >
                    <span>
                      {DayOfWeekLabelMap[range.day].abbr} {formatRange(range)}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      type="button"
                      onClick={() => removeTimeRange(idx)}
                      aria-label="Remove time range"
                      title="Remove"
                    >
                      ×
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="p-4 pb-8">
            <div className="flex items-center gap-2">
              <Checkbox
                id="untaken-only"
                name="untaken-only"
                checked={isUntakenTeachersOnlyChecked}
                onCheckedChange={(checked) => {
                  onUntakenTeachersOnlyChange?.(booleanOnlyValueOf(checked));
                }}
              />
              <Label htmlFor="untaken-only" className="block">
                Show untaken teachers only
              </Label>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="w-full rounded-none"
            type="button"
            onClick={() => {
              setTimeRanges([]);
              onClear?.();
            }}
          >
            Clear selection
          </Button>
        </PopoverContent>
      </Popover>
    </>
  );
};
